import React, { useState } from 'react';
import {
  FiChevronLeft,
  FiChevronRight,
  FiUsers,
  FiUser,
  FiSearch,
  FiTrendingUp,
  FiLink,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';

/**
 * ReferralSponsors Component
 * Lists sponsors who have referral activity, with a quick view of their chains
 */
const ReferralSponsors = ({
  sponsors = [],
  totalCount = 0,
  page = 1,
  limit = 10,
  onPageChange,
  loading,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSponsorId, setExpandedSponsorId] = useState(null);

  const totalPages = Math.ceil(totalCount / limit);

  const filteredSponsors = sponsors.filter((s) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      s.username?.toLowerCase().includes(term) ||
      s.sponsor_user_id?.toLowerCase().includes(term) ||
      String(s.sponsor_numeric_id || '').toLowerCase().includes(term)
    );
  });

  const toggleExpanded = (id) => {
    setExpandedSponsorId((prev) => (prev === id ? null : id));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-purple-500 text-white p-2 rounded-lg">
              <FiUsers className="text-lg" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Sponsors with Referrals</h3>
              <p className="text-sm text-gray-500">{totalCount} sponsors with referral activity</p>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by sponsor or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full md:w-auto"
            />
          </div>
        </div>
      </div>

      {/* Sponsors Grid */}
      <div className="p-4">
        {filteredSponsors.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FiUsers className="mx-auto text-3xl mb-2 text-gray-300" />
            <p>No sponsors found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
            {filteredSponsors.map((sponsor, index) => {
              const id = sponsor.sponsor_user_id || sponsor.user_id || index;
              const isExpanded = expandedSponsorId === id;
              const chain = sponsor.chain || {};
              const upstream = chain.upstream || null;
              const downstreamOnboarding = chain.downstream_onboarding || [];
              const downstreamTransactions = chain.downstream_transactions || [];

              return (
                <div
                  key={id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-gradient-to-br from-white to-gray-50 flex flex-col"
                >
                  {/* Sponsor header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-purple-100 p-2 rounded-full">
                        <FiUser className="text-purple-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">@{sponsor.username}</p>
                        <p className="text-xs text-gray-400 truncate max-w-[180px]" title={sponsor.sponsor_user_id}>
                          {String(sponsor.sponsor_user_id || '').startsWith('instagram:')
                            ? 'via Instagram'
                            : String(sponsor.sponsor_user_id || '').startsWith('facebook:')
                            ? 'via Facebook'
                            : 'Sponsor'}
                        </p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 text-right">
                      <p className="font-mono">ID: {sponsor.sponsor_numeric_id ?? 'N/A'}</p>
                      <p className="mt-1 flex items-center gap-1 text-green-600">
                        <FiTrendingUp className="text-xs" />
                        <span className="font-semibold">{sponsor.total_referrals ?? 0}</span>
                        <span className="text-[10px] uppercase tracking-wide">referrals</span>
                      </p>
                    </div>
                  </div>

                  {/* Summary badges */}
                  <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                    <div className="bg-indigo-50 text-indigo-700 rounded-md px-2 py-1 flex items-center justify-between">
                      <span>By Type</span>
                      <span className="font-mono text-[10px]">
                        {Object.keys(sponsor.by_type || {}).length || '-'} types
                      </span>
                    </div>
                    <div className="bg-emerald-50 text-emerald-700 rounded-md px-2 py-1 flex items-center justify-between">
                      <span>By Level</span>
                      <span className="font-mono text-[10px]">
                        {Object.keys(sponsor.by_level || {}).length || '-'} levels
                      </span>
                    </div>
                  </div>

                  {/* Expandable chain section */}
                  <button
                    type="button"
                    onClick={() => toggleExpanded(id)}
                    className="mt-auto w-full flex items-center justify-between text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md px-2 py-1 border border-blue-100"
                  >
                    <span className="flex items-center gap-1">
                      <FiLink className="text-sm" />
                      Referral chain
                    </span>
                    {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                  </button>

                  {isExpanded && (
                    <div className="mt-3 text-xs space-y-2 border-t border-gray-100 pt-3">
                      {/* Upstream */}
                      <div>
                        <p className="font-semibold text-gray-700 mb-1">Upstream (who referred this sponsor)</p>
                        {upstream ? (
                          <div className="flex items-center gap-2 bg-blue-50 rounded-md px-2 py-1">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-[11px] font-bold">
                              U
                            </span>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-800 truncate">@{upstream.username}</p>
                              <p className="text-[10px] text-gray-500 truncate">{upstream.user_id}</p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-gray-400">No upstream referrer</p>
                        )}
                      </div>

                      {/* Downstream onboarding */}
                      <div>
                        <p className="font-semibold text-gray-700 mb-1 flex items-center gap-1">
                          Downstream Onboarding
                          <span className="bg-gray-100 text-gray-600 rounded-full px-2 py-[1px] text-[10px]">
                            {downstreamOnboarding.length}
                          </span>
                        </p>
                        {downstreamOnboarding.length === 0 ? (
                          <p className="text-gray-400">No onboarding referrals</p>
                        ) : (
                          <div className="space-y-1 max-h-24 overflow-y-auto pr-1">
                            {downstreamOnboarding.map((u, idx) => (
                              <div
                                key={u.user_id || idx}
                                className="flex items-center gap-2 bg-green-50 rounded-md px-2 py-1"
                              >
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-500 text-white text-[10px] font-bold">
                                  {idx + 1}
                                </span>
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-800 truncate">@{u.username}</p>
                                  <p className="text-[10px] text-gray-500 truncate">{u.user_id}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Downstream transactions */}
                      <div>
                        <p className="font-semibold text-gray-700 mb-1 flex items-center gap-1">
                          Downstream Transactions
                          <span className="bg-gray-100 text-gray-600 rounded-full px-2 py-[1px] text-[10px]">
                            {downstreamTransactions.length}
                          </span>
                        </p>
                        {downstreamTransactions.length === 0 ? (
                          <p className="text-gray-400">No referral transactions</p>
                        ) : (
                          <div className="space-y-1 max-h-24 overflow-y-auto pr-1">
                            {downstreamTransactions.map((t, idx) => (
                              <div
                                key={t.id || idx}
                                className="flex items-center justify-between bg-orange-50 rounded-md px-2 py-1"
                              >
                                <div className="min-w-0 mr-2">
                                  <p className="font-medium text-gray-800 truncate text-[11px]">
                                    {t.type || 'transaction'}
                                  </p>
                                  {t.referred_user_id && (
                                    <p className="text-[10px] text-gray-500 truncate">
                                      → {t.referred_user_id}
                                    </p>
                                  )}
                                </div>
                                {typeof t.amount !== 'undefined' && (
                                  <p className="text-[11px] font-semibold text-orange-600 whitespace-nowrap">
                                    ₹{Number(t.amount).toFixed(0)}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalCount)} of {totalCount}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page === 1}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiChevronLeft />
            </button>

            <div className="flex items-center gap-1">
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium ${
                      page === pageNum
                        ? 'bg-blue-500 text-white'
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page === totalPages}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralSponsors;
